import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './CustomTimePicker.module.css';
import ClockIcon from 'assets/images/clock.png';

const CustomTimePicker = ({
  label,
  style,
  value,
  placeholder,
  onTimeChange,
  error,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState('--');
  const [selectedMinute, setSelectedMinute] = useState('--');
  const timePickerRef = useRef(null);
  const hourOptionsRef = useRef(null);
  const minuteOptionsRef = useRef(null);

  // Initialize selectedHour and selectedMinute from value prop
  useEffect(() => {
    if (value && value.includes(':')) {
      const [hour, minute] = value.split(':');
      setSelectedHour(hour.padStart(2, '0'));
      setSelectedMinute(minute.padStart(2, '0'));
    } else {
      // Set default time to 09:00 if no value
      setSelectedHour('09');
      setSelectedMinute('00');
    }
  }, [value]);

  // Scroll to selected values when dropdown opens
  const scrollToSelectedValues = () => {
    setTimeout(() => {
      if (hourOptionsRef.current && selectedHour !== '--') {
        const hourIndex = parseInt(selectedHour, 10);
        const hourElement = hourOptionsRef.current.children[hourIndex];
        if (hourElement) {
          hourElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }

      if (minuteOptionsRef.current && selectedMinute !== '--') {
        const minuteIndex = parseInt(selectedMinute, 10);
        const minuteElement = minuteOptionsRef.current.children[minuteIndex];
        if (minuteElement) {
          minuteElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }
    }, 50); // Small delay to ensure DOM is ready
  };

  const handleToggleDropdown = () => {
    const newDropdownState = !isDropdownOpen;
    setIsDropdownOpen(newDropdownState);

    if (newDropdownState) {
      scrollToSelectedValues();
    }
  };

  const handleTimeSelect = () => {
    const formattedTime = `${selectedHour}:${selectedMinute}`;
    onTimeChange && onTimeChange(formattedTime);
    setIsDropdownOpen(false);
  };

  const handleClickOutside = (e) => {
    if (timePickerRef.current && !timePickerRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <div
      className={styles.timePickerContainer}
      ref={timePickerRef}
      style={style}
    >
      <label className={styles.label}>{label}</label>
      <div className={styles.inputWrapper} onClick={handleToggleDropdown}>
        <input
          type="text"
          className={styles.input}
          value={value}
          placeholder={placeholder}
          readOnly
        />
        <img className={styles.icon} src={ClockIcon} alt="icon_clock" />
      </div>

      {isDropdownOpen && (
        <div className={styles.dropdown}>
          <div className={styles.dropdownColumns}>
            <div className={styles.dropdownColumn}>
              <span>Hour</span>
              <div className={styles.options} ref={hourOptionsRef}>
                {Array.from({ length: 24 }, (_, i) =>
                  String(i).padStart(2, '0')
                ).map((hour) => (
                  <div
                    key={hour}
                    className={`${styles.dropdownItem} ${selectedHour === hour ? styles.selected : ''}`}
                    onClick={() => setSelectedHour(hour)}
                    onMouseLeave={() => {}} // Force re-render to clear hover state
                  >
                    {hour}
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.dropdownColumn}>
              <span>Minute</span>
              <div className={styles.options} ref={minuteOptionsRef}>
                {Array.from({ length: 60 }, (_, i) =>
                  String(i).padStart(2, '0')
                ).map((minute) => (
                  <div
                    key={minute}
                    className={`${styles.dropdownItem} ${selectedMinute === minute ? styles.selected : ''}`}
                    onClick={() => setSelectedMinute(minute)}
                    onMouseLeave={() => {}} // Force re-render to clear hover state
                  >
                    {minute}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button
            className={styles.confirmButton}
            onClick={handleTimeSelect}
            disabled={selectedHour === '--' || selectedMinute === '--'}
          >
            Confirm
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
};

CustomTimePicker.propTypes = {
  label: PropTypes.string,
  style: PropTypes.object,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onTimeChange: PropTypes.func,
  error: PropTypes.string,
};

export default CustomTimePicker;
